"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, Clock, ArrowRight, Sparkles, TrendingUp, Layers, Volume2, Wine, House as Home } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
interface SpecialtyItem {
  id: string;
  title: string;
  description: string;
  timeline: string;
  spec: string;
}
const specialties: SpecialtyItem[] = [{
  id: "guest-suites",
  title: "Guest Suites & In-Law Quarters",
  description: "Private lower-level living with full bathrooms, walk-in closets, sound dampening, and code-compliant Decatur egress windows for natural light.",
  timeline: "6–8 Weeks",
  spec: "Meets City of Decatur R-3 egress requirements & dedicated HVAC zoning"
}, {
  id: "home-theaters",
  title: "Acoustic Home Theaters",
  description: "Engineered media sanctuaries with decoupled wall framing, acoustic drywall, concealed conduit routing, and dimmable architectural cove lighting.",
  timeline: "5–7 Weeks",
  spec: "STC 55+ sound insulation rating & 4K projector pre-wire"
}, {
  id: "wet-bars",
  title: "Custom Wet Bars & Wine Cellars",
  description: "Entertaining hubs with handcrafted white oak cabinetry, quartz countertops, integrated wine chillers, dishwasher drawers, and tile backsplashes.",
  timeline: "4–6 Weeks",
  spec: "Dedicated GFCI sub-circuits & moisture-sealed underlayment"
}, {
  id: "rec-rooms",
  title: "Multi-Purpose Recreation Rooms",
  description: "Flexible family play areas, home gyms, and creative studios engineered with high-impact luxury vinyl plank flooring and recessed LED grids.",
  timeline: "4–6 Weeks",
  spec: "100% waterproof subfloor barriers & impact-resistant trim"
}];
export default function Basement() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUpVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="basement-remodeling" data-nav="light" className="bg-background text-foreground relative overflow-hidden py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Editorial & Specialties */}
          <motion.div variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7 flex flex-col justify-between">
            <div>
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="h-0.5 w-6 bg-primary" />
                <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-muted-foreground font-sans">
                  Basement Remodeling & Finishing
                </span>
              </div>

              {/* Main Heading */}
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground leading-[1.15] mb-6">
                Transforming lower levels into light-filled living spaces
              </h2>

              {/* Intro Text */}
              <p className="font-sans text-base sm:text-lg text-muted-foreground leading-relaxed mb-8">
                Older Decatur foundations present unique architectural challenges—from below-grade moisture and low joist clearances to strict egress requirements. We solve each with perimeter drainage, slimline mechanical bulkheads, and custom window wells that bring genuine daylight into your lower level.
              </p>

              {/* Accordion Specialties */}
              <div className="mb-10">
                <Accordion type="single" collapsible defaultValue="guest-suites" className="w-full space-y-3">
                  {specialties.map((item, index) => <AccordionItem key={item.id} value={item.id} data-index={index} className="border border-border rounded-lg bg-card px-5 transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-xs overflow-hidden">
                      <AccordionTrigger className="py-4 hover:no-underline text-foreground font-sans text-base font-semibold">
                        <div className="flex items-center gap-3 text-left">
                          {index === 0 && <Home className="size-4 text-primary shrink-0" />}
                          {index === 1 && <Volume2 className="size-4 text-primary shrink-0" />}
                          {index === 2 && <Wine className="size-4 text-primary shrink-0" />}
                          {index === 3 && <Layers className="size-4 text-primary shrink-0" />}
                          <span>{item.title}</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-1 pb-4 text-muted-foreground">
                        <p className="font-sans text-sm sm:text-base leading-relaxed mb-4">
                          {item.description}
                        </p>
                        
                        {/* Specs & Timeline Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border/60 text-xs font-medium">
                          <div className="flex items-center gap-2 text-foreground">
                            <ShieldCheck className="size-3.5 text-primary shrink-0" />
                            <span className="line-clamp-1">{item.spec}</span>
                          </div>
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Clock className="size-3.5 text-primary shrink-0" />
                            <span>Typical schedule: {item.timeline}</span>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </div>

              {/* Action CTAs */}
              <div className="flex flex-wrap items-center gap-5 pt-2">
                <Button asChild size="lg" className="rounded-md font-semibold text-sm sm:text-base px-6 shadow-xs">
                  <Link to="/ContactPage">
                    Schedule a Basement Consultation
                  </Link>
                </Button>
                
                <Link to="/ProjectsPage" className="inline-flex items-center gap-2 font-semibold text-sm sm:text-base text-foreground hover:text-primary transition-colors py-2 group">
                  <span>View Basement Gallery</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Visual Showcase & Metric Badge */}
          <motion.div variants={fadeUpVariant} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-5 relative mt-4 lg:mt-0">
            {/* Primary Framed Image */}
            <div className="relative rounded-xl overflow-hidden border border-border bg-muted shadow-lg">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/finished_basement_renovation_wet_bar.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/finished_basement_renovation_wet_bar.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/finished_basement_renovation_wet_bar.png-wvc-sizes" alt="Finished daylight basement renovation in Decatur featuring custom white oak millwork wet bar and recessed lighting" loading="lazy" decoding="async" className="w-full h-[460px] sm:h-[540px] lg:h-[580px] object-cover object-center" />

              {/* Subtle Warm Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent pointer-events-none" />

              {/* Floating Architectural Metric Glass Badge */}
              <div className="absolute bottom-6 left-6 right-6 p-5 sm:p-6 rounded-xl bg-card/95 backdrop-blur-md border border-border shadow-xl">
                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-lg bg-secondary text-primary shrink-0">
                    <TrendingUp className="size-6 text-primary" />
                  </div>
                  <div>
                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">
                      <Sparkles className="size-3 text-primary" />
                      <span>Local Value Assessment</span>
                    </div>
                    <p className="font-serif text-lg sm:text-xl font-semibold text-card-foreground leading-snug">
                      Average Decatur Added Value: +20–25% Home Equity
                    </p>
                    <p className="font-sans text-xs text-muted-foreground mt-1">
                      Finished square footage substantially lowers cost-per-foot relative to ground-up additions.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Architectural Hairline Accent */}
            <div className="hidden sm:block absolute -top-3 -right-3 w-24 h-24 border-t-2 border-r-2 border-primary/40 rounded-tr-xl pointer-events-none" />
          </motion.div>
        </div>
      </div>
    </section>;
}